import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Imagekit } from "../../Pages/CommonPages/ImageKit";
import { createSlug } from "../../Utils/HelperFunctions";
import useIsDesktop from "../../Hooks/useIsDesktop";

/**
 * CraftClusterRow
 *
 * Editorial horizontal shelf of craft-cluster cards ("Shop <X> by Craft"),
 * fed from get_home_page (clothingCrafts / jewelryCrafts).
 *
 * @param {string} title       - e.g. "Shop Clothing by"
 * @param {string} highlight   - highlighted word, e.g. "Craft"
 * @param {string} subtitle    - small caption under the heading
 * @param {Array}  items       - [{ id, name, origin, image }] — ONE PAGE, not the full set
 * @param {number} total       - totalElements from the paginated envelope
 * @param {string} seeAllHref  - route for the "See all crafts" card
 */
export default function CraftClusterRow({
  title,
  highlight,
  subtitle,
  items = [],
  total,
  seeAllHref,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  if (!items?.length) return null;

  // Cap the visible cards per breakpoint (mobile 4 / desktop 7).
  //
  // `items` is ONE PAGE of get_home_page, which hardcodes pageSize=4 — so
  // items.length can never exceed 4 and the old `items.length > cap` test was
  // arithmetically dead (4 > 4 and 4 > 6 are both false). The "is there more?"
  // question can only be answered by the envelope's totalElements.
  //
  // Compare against the cards actually on screen, not `cap`: while the API still
  // sends fewer than the cap, a rail with 6 crafts shows 4 and must still offer a
  // way to reach the other 2. Once the backend raises pageSize past the cap this
  // naturally becomes "more than 7 on desktop".
  const cap = isDesktop ? 7 : 4;
  const visibleItems = items.slice(0, cap);
  const totalCount = total ?? items.length; // fall back if envelope is absent
  const showSeeAll = totalCount > visibleItems.length;

  return (
    <section className="pt-[6px] pb-[10px]">
      <div className="px-4 pb-[11px]">
        <h2 className="text-[17px] font-[500] text-ink-900 tracking-[-0.01em]">
          {title}{" "}
          <span className="text-coral-ink">
            {highlight || t("craftRow.craft")}
          </span>
        </h2>
        {subtitle && (
          <div className="text-[11px] text-ink-500 mt-[3px]">{subtitle}</div>
        )}
      </div>

      <div className="flex gap-[11px] overflow-x-auto no-scrollbar pt-[2px] px-4 pb-[8px] snap-x snap-mandatory scroll-pl-4">
        {visibleItems.map((it) => (
          <button
            key={it.id ?? it.name}
            type="button"
            onClick={() => navigate(`/craft/${createSlug(it.name)}/${it.id}`)}
            className="flex-none w-[148px] snap-start rounded-lg overflow-hidden relative text-left"
            style={{ boxShadow: "0 4px 18px rgba(31,33,46,.08)" }}
          >
            <div className="w-full aspect-[3/4] bg-[#F5F5F5]">
              <Imagekit
                IMGName={it.image}
                className="w-full h-full object-cover object-top"
                eager
              />
            </div>
            <div
              className="absolute left-0 right-0 bottom-0 px-[11px] py-[10px] text-white"
              style={{
                background: "linear-gradient(transparent, rgba(31,33,46,.74))",
              }}
            >
              <div className="text-[13px] font-[500] tracking-[-0.01em] truncate">
                {it.name}
              </div>
              <div className="text-[9.5px] text-white/85 mt-[1px] truncate">
                {it.origin || it.region}
              </div>
            </div>
          </button>
        ))}

        {/* See all crafts — only when crafts overflow the visible run */}
        {showSeeAll && (
          <button
            type="button"
            onClick={() => navigate(seeAllHref)}
            className="flex-none w-[148px] snap-start rounded-lg overflow-hidden"
            style={{ boxShadow: "0 4px 18px rgba(31,33,46,.08)" }}
          >
            <div className="w-full aspect-[3/4] bg-[#F4F5FF] flex items-center justify-center">
              <span className="text-[12px] font-[500] text-s-primary text-center px-[10px]">
                {t("craftRow.seeAll")}
              </span>
            </div>
          </button>
        )}
      </div>
    </section>
  );
}
